import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiService } from '../services/apiService';
import { useAuth } from '../context/AuthContext';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Tooltip,
    Legend
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Tooltip,
    Legend
);

const formatCurrency = (value) => {
    return new Intl.NumberFormat('ja-JP', {
        style: 'currency',
        currency: 'JPY',
        minimumFractionDigits: 0
    }).format(value ?? 0);
};

export default function TalentBillingChart({ talentId, months = 36, refreshKey = 0 }) {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [series, setSeries] = useState([]);
    const [meta, setMeta] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [editingMonth, setEditingMonth] = useState(null);
    const [editAmount, setEditAmount] = useState('');
    const [editReason, setEditReason] = useState('');
    const [actionLoadingMonth, setActionLoadingMonth] = useState('');
    const [actionError, setActionError] = useState('');
    const [localRefreshKey, setLocalRefreshKey] = useState(0);

    const canManualEdit = user?.role === 'Admin' || user?.role === 'Sales';

    useEffect(() => {
        if (!talentId) return;
        let isActive = true;
        setIsLoading(true);
        setError('');

        apiService.getTalentBillingSeries(talentId, months)
            .then((data) => {
                if (!isActive) return;
                setSeries(data?.series || []);
                setMeta({
                    startMonth: data?.startMonth,
                    endMonth: data?.endMonth
                });
            })
            .catch((err) => {
                if (!isActive) return;
                setError(err.message || t('billingTrendError'));
            })
            .finally(() => {
                if (!isActive) return;
                setIsLoading(false);
            });

        return () => {
            isActive = false;
        };
    }, [talentId, months, refreshKey, localRefreshKey, t]);

    const hasPaused = useMemo(() => series.some(item => item.isPaused), [series]);
    const hasNonBilled = useMemo(() => series.some(item => !item.isBilled), [series]);
    const hasManual = useMemo(() => series.some(item => item.isManualOverride), [series]);

    const chartData = useMemo(() => {
        const labels = series.map(item => item.month);
        const amounts = series.map(item => item.amount ?? 0);
        const pausedPoints = series.map(item => (item.isPaused ? (item.amount ?? 0) : null));
        const manualPoints = series.map(item => (item.isManualOverride ? (item.amount ?? 0) : null));

        return {
            labels,
            datasets: [
                {
                    label: t('billingTrendSeriesLabel'),
                    data: amounts,
                    borderColor: '#3B82F6',
                    backgroundColor: 'rgba(59, 130, 246, 0.15)',
                    pointRadius: 3,
                    pointHoverRadius: 4,
                    tension: 0.25
                },
                {
                    label: t('billingTrendPausedLabel'),
                    data: pausedPoints,
                    showLine: false,
                    pointRadius: 4,
                    pointHoverRadius: 5,
                    borderColor: '#EF4444',
                    backgroundColor: '#EF4444'
                },
                {
                    label: '手動調整月',
                    data: manualPoints,
                    showLine: false,
                    pointRadius: 5,
                    pointHoverRadius: 6,
                    pointStyle: 'rectRounded',
                    borderColor: '#F59E0B',
                    backgroundColor: '#F59E0B'
                }
            ]
        };
    }, [series, t]);

    const chartOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top'
            },
            tooltip: {
                callbacks: {
                    label: (context) => {
                        const label = context.dataset.label || '';
                        return `${label}: ${formatCurrency(context.parsed.y)}`;
                    }
                }
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    callback: (value) => formatCurrency(value)
                }
            },
            x: {
                grid: {
                    display: false
                }
            }
        }
    }), []);

    const getStatusColor = (item) => {
        if (item.isManualOverride) return 'bg-amber-400';
        if (item.isPaused) return 'bg-red-400';
        if (item.isBilled) return 'bg-emerald-500';
        return 'bg-gray-300';
    };

    const getStatusLabel = (item) => {
        if (item.isManualOverride) return '手動調整';
        if (item.isPaused) return t('billingTrendPausedLabel');
        if (item.isBilled) return t('billingTrendBilledLabel');
        return t('billingTrendNoChargeLabel');
    };

    const startEdit = (item) => {
        setEditingMonth(item.month);
        setEditAmount(String(item.amount ?? 0));
        setEditReason(item.manualReason || '');
        setActionError('');
    };

    const cancelEdit = () => {
        setEditingMonth(null);
        setEditAmount('');
        setEditReason('');
    };

    const saveOverride = async (month) => {
        const amount = Number(editAmount);
        const reason = editReason.trim();
        if (!Number.isFinite(amount) || amount < 0) {
            setActionError('金額は 0 以上の数値で入力してください');
            return;
        }
        if (!reason) {
            setActionError('変更理由は必須です');
            return;
        }

        try {
            setActionError('');
            setActionLoadingMonth(month);
            await apiService.upsertTalentBillingOverride(talentId, {
                month,
                amount,
                reason
            });
            cancelEdit();
            setLocalRefreshKey(prev => prev + 1);
        } catch (e) {
            setActionError(e.message || '手動調整の保存に失敗しました');
        } finally {
            setActionLoadingMonth('');
        }
    };

    const restoreAuto = async (month) => {
        const reasonInput = window.prompt('自動計算に戻す理由を入力してください');
        const reason = (reasonInput || '').trim();
        if (!reason) {
            return;
        }

        try {
            setActionError('');
            setActionLoadingMonth(month);
            await apiService.restoreTalentBillingOverride(talentId, { month, reason });
            if (editingMonth === month) {
                cancelEdit();
            }
            setLocalRefreshKey(prev => prev + 1);
        } catch (e) {
            setActionError(e.message || '自動計算への復帰に失敗しました');
        } finally {
            setActionLoadingMonth('');
        }
    };

    return (
        <div className="mt-4 rounded-lg border border-gray-200 bg-white p-4">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <h4 className="text-sm font-semibold text-gray-700">{t('billingTrend')}</h4>
                <div className="flex items-center gap-2">
                    {meta?.startMonth && meta?.endMonth && (
                        <span className="text-xs text-gray-500">
                            {t('billingTrendRange', { start: meta.startMonth, end: meta.endMonth })}
                        </span>
                    )}
                    <button
                        type="button"
                        onClick={() => setDetailsOpen(prev => !prev)}
                        className="text-xs px-2 py-1 border border-gray-300 rounded hover:bg-gray-50"
                    >
                        {detailsOpen ? '月明細を閉じる' : '月明細を開く'}
                    </button>
                </div>
            </div>

            {isLoading && (
                <p className="text-sm text-gray-500">{t('billingTrendLoading')}</p>
            )}

            {!isLoading && error && (
                <p className="text-sm text-red-500">{t('billingTrendError')}: {error}</p>
            )}

            {!isLoading && !error && series.length === 0 && (
                <p className="text-sm text-gray-500">{t('billingTrendNoData')}</p>
            )}

            {!isLoading && !error && series.length > 0 && (
                <div className="h-64">
                    <Line data={chartData} options={chartOptions} />
                </div>
            )}

            {!isLoading && !error && series.length > 0 && (
                <div className="mt-4">
                    <div className="flex h-3 overflow-hidden rounded-full border border-gray-200">
                        {series.map((item) => (
                            <div
                                key={item.month}
                                className={`${getStatusColor(item)} flex-1`}
                                title={`${item.month} · ${getStatusLabel(item)} · ${formatCurrency(item.amount)}`}
                            />
                        ))}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-4 text-xs text-gray-500">
                        <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-emerald-500"></span>{t('billingTrendBilledLabel')}</span>
                        {hasManual && (
                            <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-amber-400"></span>手動調整月</span>
                        )}
                        {hasPaused && (
                            <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-red-400"></span>{t('billingTrendPausedLabel')}</span>
                        )}
                        {hasNonBilled && (
                            <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-gray-300"></span>{t('billingTrendNoChargeLabel')}</span>
                        )}
                    </div>
                </div>
            )}

            {!isLoading && !error && detailsOpen && series.length > 0 && (
                <div className="mt-4 border border-gray-200 rounded-md overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-xs">
                            <thead className="bg-gray-50 text-gray-600">
                                <tr>
                                    <th className="px-3 py-2 text-left">月</th>
                                    <th className="px-3 py-2 text-left">状態</th>
                                    <th className="px-3 py-2 text-right">自動金額</th>
                                    <th className="px-3 py-2 text-right">確定金額</th>
                                    <th className="px-3 py-2 text-left">规则说明</th>
                                    {canManualEdit && <th className="px-3 py-2 text-left">手動調整</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {series.map((item) => {
                                    const isEditing = editingMonth === item.month;
                                    const isBusy = actionLoadingMonth === item.month;
                                    return (
                                        <tr key={item.month} className="border-t border-gray-100 align-top">
                                            <td className="px-3 py-2 whitespace-nowrap">{item.month}</td>
                                            <td className="px-3 py-2 whitespace-nowrap">
                                                <span className={`inline-flex items-center rounded-full px-2 py-0.5 ${
                                                    item.isManualOverride
                                                        ? 'bg-amber-100 text-amber-700'
                                                        : item.isPaused
                                                            ? 'bg-red-100 text-red-700'
                                                            : item.isBilled
                                                                ? 'bg-emerald-100 text-emerald-700'
                                                                : 'bg-gray-100 text-gray-700'
                                                }`}>
                                                    {getStatusLabel(item)}
                                                </span>
                                                {item.pauseType && (
                                                    <span className="ml-2 text-gray-500">({item.pauseType})</span>
                                                )}
                                            </td>
                                            <td className="px-3 py-2 text-right whitespace-nowrap">{formatCurrency(item.autoAmount ?? item.amount ?? 0)}</td>
                                            <td className="px-3 py-2 text-right whitespace-nowrap font-semibold">{formatCurrency(item.amount ?? 0)}</td>
                                            <td className="px-3 py-2 text-gray-600 min-w-[280px]">
                                                <div>{item.reason}</div>
                                                {item.isManualOverride && item.manualUpdatedBy && (
                                                    <div className="text-[11px] text-gray-500 mt-1">
                                                        更新者: {item.manualUpdatedBy}
                                                    </div>
                                                )}
                                            </td>
                                            {canManualEdit && (
                                                <td className="px-3 py-2 min-w-[260px]">
                                                    {isEditing ? (
                                                        <div className="space-y-2">
                                                            <input
                                                                type="number"
                                                                min="0"
                                                                step="1"
                                                                value={editAmount}
                                                                onChange={(e) => setEditAmount(e.target.value)}
                                                                className="w-full border border-gray-300 rounded px-2 py-1"
                                                                placeholder="金額"
                                                            />
                                                            <input
                                                                type="text"
                                                                value={editReason}
                                                                onChange={(e) => setEditReason(e.target.value)}
                                                                className="w-full border border-gray-300 rounded px-2 py-1"
                                                                placeholder="変更理由（必須）"
                                                            />
                                                            <div className="flex gap-2">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => saveOverride(item.month)}
                                                                    disabled={isBusy}
                                                                    className="px-2 py-1 rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60"
                                                                >
                                                                    保存
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={cancelEdit}
                                                                    disabled={isBusy}
                                                                    className="px-2 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-60"
                                                                >
                                                                    キャンセル
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="flex flex-wrap gap-2">
                                                            <button
                                                                type="button"
                                                                onClick={() => startEdit(item)}
                                                                disabled={isBusy}
                                                                className="px-2 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-60"
                                                            >
                                                                金額を編集
                                                            </button>
                                                            {item.isManualOverride && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => restoreAuto(item.month)}
                                                                    disabled={isBusy}
                                                                    className="px-2 py-1 rounded border border-amber-300 text-amber-700 hover:bg-amber-50 disabled:opacity-60"
                                                                >
                                                                    自動へ戻す
                                                                </button>
                                                            )}
                                                        </div>
                                                    )}
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    {actionError && (
                        <div className="border-t border-red-100 bg-red-50 px-3 py-2 text-xs text-red-600">
                            {actionError}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
